import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import { Grid, TextField, MenuItem, ListSubheader } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { NkSelect } from '@nordkap/nordkap-ui';
import _ from 'lodash';
import api from '../../../infrastructure/api/api';
import { emptyGuid, anyGuid, positionTypes } from '../../../utils/constants';
import { combineClasses } from '../../../utils/styling';
import { useStyles } from './styles';

const PositionsFiltering = props => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const notSelectedOption = useMemo(
    () => ({
      value: anyGuid,
      title: <em>{t('massUpdatesPage.notSelected')}</em>,
    }),
    [t]
  );

  const [portfolios, setPortfolios] = useState([notSelectedOption]);

  const [positionTemplateId, setPositionTemplateId] = useState(emptyGuid);
  const [positionType, setPositionType] = useState(anyGuid);
  const [counterparty, setCounterparty] = useState(anyGuid);
  const [portfolio, setPortfolio] = useState(anyGuid);

  useEffect(() => {
    api.get('portfolios', enqueueSnackbar, t).then(portfoliosResult => {
      if (portfoliosResult !== null) {
        setPortfolios(
          [notSelectedOption].concat(
            portfoliosResult.map(p => ({ value: p.name, title: p.name }))
          )
        );
      }
    });
  }, [enqueueSnackbar, notSelectedOption, t]);

  const counterparties = [notSelectedOption].concat(
    props.counterparties
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(c => ({ value: c.name, title: c.name }))
  );

  const templateOptionLabel = positionTemplate =>
    `${
      (positionTemplate.archived
        ? `[${t('positionTemplate.archived')}] `
        : '') + positionTemplate.name
    } ${t('fieldsAndTemplatesPage.usage', {
      positionCount: positionTemplate.positionCount,
    })}`;

  return (
    <div className={classes.FilterPositionsRow}>
      <Grid container item xs={12}>
        <Grid container item xs={3}>
          <div className={classes.Field}>
            <label
              className={combineClasses([classes.Bold, classes.UpperCase])}
              htmlFor="select-position-template"
            >
              {t('massUpdatesPage.accountingTemplate')}
            </label>
            <TextField
              variant="outlined"
              id="select-position-template"
              fullWidth
              margin="dense"
              value={positionTemplateId}
              onChange={event => {
                setPositionTemplateId(event.target.value);
                props.setFilters(prevFilters => ({
                  ...prevFilters,
                  positionTemplateId: event.target.value,
                }));
                props.searchPositions(
                  event.target.value,
                  positionType,
                  counterparty,
                  portfolio
                );
              }}
              select
            >
              <MenuItem value={anyGuid}>
                <em>{t('massUpdatesPage.allTemplates')}</em>
              </MenuItem>
              <MenuItem value={emptyGuid}>
                {t('massUpdatesPage.unassignedTemplate')}
              </MenuItem>
              {positionTypes.map(pt => {
                const optionsPropertyName = `${_.lowerFirst(pt)}Options`;

                return [
                  <ListSubheader
                    className={classes.PositionTemplateGroupName}
                    key={pt}
                    disableSticky
                  >
                    {t(`positionType.${pt}`)}
                  </ListSubheader>,
                  props.templateOptions[optionsPropertyName] &&
                    props.templateOptions[optionsPropertyName].map(template => (
                      <MenuItem
                        key={template.positionTemplateId}
                        value={template.positionTemplateId}
                      >
                        {templateOptionLabel(template)}
                      </MenuItem>
                    )),
                ].flatMap(x => x);
              })}
            </TextField>
          </div>
        </Grid>
        <Grid container item xs={3}>
          <div className={classes.Field}>
            <label
              className={combineClasses([classes.Bold, classes.UpperCase])}
              htmlFor="select-portfolios"
            >
              {t('massUpdatesPage.portfolio')}
            </label>
            <NkSelect
              variant="outlined"
              name="select-portfolio"
              itemPropName="value"
              itemTitlePropName="title"
              items={portfolios}
              value={portfolio}
              onChange={event => {
                setPortfolio(event.target.value);
                props.setFilters(prevFilters => ({
                  ...prevFilters,
                  portfolio: event.target.value,
                }));
                props.searchPositions(
                  positionTemplateId,
                  positionType,
                  counterparty,
                  event.target.value
                );
              }}
            />
          </div>
        </Grid>
        <Grid container item xs={3}>
          <div className={classes.Field}>
            <label
              className={combineClasses([classes.Bold, classes.UpperCase])}
              htmlFor="select-position-type"
            >
              {t('positionTemplate.positionType')}
            </label>
            <NkSelect
              variant="outlined"
              name="select-position-type"
              itemPropName="value"
              itemTitlePropName="title"
              items={[notSelectedOption].concat(
                positionTypes.map(pt => ({
                  value: pt,
                  title: t(`positionType.${pt}`),
                }))
              )}
              value={positionType}
              onChange={event => {
                setPositionType(event.target.value);
                props.setFilters(prevFilters => ({
                  ...prevFilters,
                  positionType: event.target.value,
                }));
                props.searchPositions(
                  positionTemplateId,
                  event.target.value,
                  counterparty,
                  portfolio
                );
              }}
            />
          </div>
        </Grid>
        <Grid container item xs={3}>
          <div className={classes.Field}>
            <label
              className={combineClasses([classes.Bold, classes.UpperCase])}
              htmlFor="select-counterparty"
            >
              {t('massUpdatesPage.counterparty')}
            </label>
            <NkSelect
              variant="outlined"
              name="select-counterparty"
              itemPropName="value"
              itemTitlePropName="title"
              items={counterparties}
              value={counterparty}
              onChange={event => {
                setCounterparty(event.target.value);
                props.setFilters(prevFilters => ({
                  ...prevFilters,
                  counterparty: event.target.value,
                }));
                props.searchPositions(
                  positionTemplateId,
                  positionType,
                  event.target.value,
                  portfolio
                );
              }}
            />
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

PositionsFiltering.propTypes = {
  templateOptions: PropTypes.object,
  searchPositions: PropTypes.func,
  setFilters: PropTypes.func,
  counterparties: PropTypes.arrayOf(PropTypes.object),
};

export default PositionsFiltering;
